import React, { useEffect, useState } from 'react';
import './Toast.css';

export interface ToastData {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'tip' | 'achievement' | 'encourage';
  duration?: number; // ms, default 3000
  icon?: string;
}

interface ToastProps {
  toast: ToastData;
  onDismiss: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onDismiss }) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const duration = toast.duration || 3000;
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => onDismiss(toast.id), 300); // Wait for exit animation
    }, duration);

    return () => clearTimeout(timer);
  }, [toast, onDismiss]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => onDismiss(toast.id), 300);
  };

  const getIcon = () => {
    if (toast.icon) return toast.icon;
    switch (toast.type) {
      case 'success': return '✓';
      case 'error': return '✗';
      case 'info': return 'ℹ';
      case 'tip': return '💡';
      case 'achievement': return '🏆';
      case 'encourage': return '💪';
      default: return '';
    }
  };

  return (
    <div
      className={`toast toast-${toast.type} ${isExiting ? 'toast-exit' : ''}`}
      onClick={handleDismiss}
    >
      <span className="toast-icon">{getIcon()}</span>
      <span className="toast-message">{toast.message}</span>
    </div>
  );
};

// Toast Container - manages multiple toasts
interface ToastContainerProps {
  toasts: ToastData[];
  onDismiss: (id: string) => void;
  position?: 'bottom' | 'top' | 'bottom-right' | 'top-right';
}

export const ToastContainer: React.FC<ToastContainerProps> = ({
  toasts,
  onDismiss,
  position = 'bottom'
}) => {
  return (
    <div className={`toast-container toast-${position}`}>
      {toasts.map(toast => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

// Hook for easy toast management
export const useToast = () => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const showToast = (
    message: string,
    type: ToastData['type'] = 'info',
    options?: { duration?: number; icon?: string; replace?: boolean }
  ) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const newToast: ToastData = {
      id,
      message,
      type,
      duration: options?.duration,
      icon: options?.icon,
    };
    // Replace mode: only show latest toast (default for quiz feedback)
    if (options?.replace !== false) {
      setToasts([newToast]);
    } else {
      setToasts(prev => [...prev, newToast]);
    }
    return id;
  };

  const dismissToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const clearAll = () => {
    setToasts([]);
  };

  return {
    toasts,
    showToast,
    dismissToast,
    clearAll,
    // Convenience methods - all replace by default to avoid stacking
    success: (msg: string, opts?: { duration?: number }) => showToast(msg, 'success', opts),
    error: (msg: string, opts?: { duration?: number }) => showToast(msg, 'error', opts),
    info: (msg: string, opts?: { duration?: number }) => showToast(msg, 'info', opts),
    tip: (msg: string, opts?: { duration?: number }) => showToast(msg, 'tip', opts),
    achievement: (msg: string, opts?: { duration?: number; icon?: string }) => showToast(msg, 'achievement', opts),
    encourage: (msg: string, opts?: { duration?: number }) => showToast(msg, 'encourage', opts),
  };
};

export default Toast;
